// PieChart.js or PieChart.tsx (if using TypeScript)
import React, { type JSX } from 'react';
import ReactECharts from 'echarts-for-react';
import { getStringGroupPath } from 'pages/WidgetManagement/common/utils/helpers';
import { seriesColors } from '@fiji/common';

function computePieGeometry(
    isFullView: boolean,
    containerSize: { width: number; height: number },
    dataLength: number
): { radius: string | number; center: Array<string | number> } {
    if (isFullView || containerSize.width === 0 || containerSize.height === 0) {
        return {
            radius: isFullView ? '55%' : '60%',
            center: isFullView ? ['30%', '50%'] : ['50%', '50%'],
        };
    }
    // Reserve enough height at the bottom for the vertical legend (one row per slice, ~24px
    // each) so the pie is sized/positioned to only ever use the space above it.
    const legendHeight = Math.min(containerSize.height * 0.6, dataLength * 24 + 16);
    const availableHeight = Math.max(50, containerSize.height - legendHeight - 8);
    const maxRadius = Math.max(20, Math.min(containerSize.width, availableHeight) / 2 - 6);
    return {
        radius: maxRadius,
        center: ['50%', Math.round(availableHeight / 2 + 4)],
    };
}

const PieChart = ({ chartWidget, chartRect }: { chartWidget: any; chartRect?: any }): JSX.Element => {
    const chartRef = React.useRef(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = React.useState<{ width: number; height: number }>({
        width: 0,
        height: 0,
    });

    // Measure the pie's own wrapping box (not just chartRect's height) so the compact (non-full-view)
    // pie can shrink its radius and shift its center up as the widget card gets smaller, keeping
    // clear of the legend below — the legend's own height doesn't shrink with the container, so a
    // fixed radius/center overlaps it once the card gets small enough.
    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const updateSize = (): void => setContainerSize({ width: el.clientWidth, height: el.clientHeight });
        updateSize();
        const observer = new ResizeObserver(updateSize);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const getSeriesData = (data: any): any[] =>
        data?.slice(0, chartWidget?.maxCount ?? 10)?.map((item: any, index: number) => ({
            name: item?.displayName,
            value: item?.value ? +item?.value : 0,
            itemStyle: { color: seriesColors[index] },
            unit: item?.unit ?? '',
            suffix: chartWidget?.multiDevice ? getStringGroupPath(item?.devicePath, item?.deviceName) : '',
        }));

    const option = React.useMemo(() => {
        const data = getSeriesData(chartWidget?.secondary);
        const isFullView = !!chartWidget?.isFullView;
        const { radius: pieRadius, center: pieCenter } = computePieGeometry(
            isFullView,
            containerSize,
            data?.length ?? 0
        );

        const legendConfig = chartWidget?.isFullView
            ? {
                  // Anchored via `left` (not `right`) so long legend text ("value + unit + name +
                  // multi-device suffix path") always grows further AWAY from the pie (to the
                  // right) instead of growing back toward it — a right-anchored legend's text
                  // extends leftward as it gets longer, which is what caused the overlap with the
                  // pie chart when values/suffixes were long. `left` fixes the gap between the pie
                  // and the legend at a constant position regardless of text length.
                  // Pie is centered at 30% with radius 55%, so its right edge sits at ~57.5% —
                  // 58% keeps a small, consistent gap without touching the pie.
                  orient: 'vertical',
                  left: '45%',
                  top: 'middle',
                  align: 'left',
                  itemGap: 14,
                  formatter: (name: string): string => {
                      const item = data?.find((d: any) => d.name === name);
                      return `{bold|${item?.value ?? 0}}${item?.unit ?? ''}{bold| ${name}} ${item?.suffix ?? ''}`;
                  },
                  textStyle: {
                      rich: {
                          bold: { fontWeight: 'bold' },
                      },
                  },
              }
            : {
                  orient: 'vertical',
                  bottom: 0,
                  left: 10,
                  itemGap: 10,
                  formatter: (name: string): string => {
                      const item = data?.find((d: any) => d.name === name);
                      return `{bold|${item?.value ?? 0}}${item?.unit ?? ''}{bold| ${name}} ${item?.suffix ?? ''}`;
                  },
                  textStyle: {
                      rich: {
                          bold: { fontWeight: 'bold' },
                      },
                  },
              };

        return {
            tooltip: {
                trigger: 'item',
                formatter: (params: any): string => {
                    const d = params.data;
                    return `
                <div style="display: flex;flex-direction: row;align-items: center;">
                    <span style="
                        display: inline-block;
                        width: 16px;
                        height: 16px;
                        margin:6px;
                        border-radius: 50%;
                        background-color: ${d?.itemStyle?.color};
                        "></span>
                    <span style="font-size:14px;">${d?.name}</span>
                    <span style="color: #555;font-size:14px; margin:6px;"><b>${d?.value}</b>${d?.unit}</span>
              </div>`;
                },
            },
            legend: legendConfig,
            series: [
                {
                    type: 'pie',
                    // In full view the legend is anchored at left: '62%', so keep the pie's
                    // center + radius comfortably inside the remaining left-hand space (instead of
                    // dead-center at 50%) to guarantee a visible gap between the pie and the legend.
                    // In compact (non-full-view) mode, radius/center are computed above from the
                    // measured container size so the pie shrinks and moves up as the card shrinks,
                    // instead of a fixed 60%/50% that can overlap the legend once the card is small.
                    radius: pieRadius,
                    center: pieCenter,
                    data: data ?? [],
                    label: { show: false },
                    itemStyle: {
                        // borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    emphasis: {
                        // itemStyle: {
                        //     shadowBlur: 10,
                        //     shadowOffsetX: 0,
                        //     shadowColor: 'rgba(0, 0, 0, 0.5)',
                        // },
                        focus: 'self',
                    },
                },
            ],
        };
    }, [chartWidget, chartRect, containerSize]);

    const chartHeight = chartWidget?.isFullView
        ? 400
        : chartRect?.chartHeight
        ? Math.floor(chartRect?.chartHeight - 50)
        : 400;

    return (
        <div ref={containerRef} style={{ width: '100%', height: chartHeight }}>
            <ReactECharts ref={chartRef} option={option} style={{ height: '100%', width: '100%' }} notMerge={true} />
        </div>
    );
};

export default PieChart;
